import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators, } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter, finalize, take } from 'rxjs/operators';

import { EnrollmentGroupService } from '@core/api/enrollment-group.service';
import { FormatHelperService } from '@core/helper/format-helper.service';
import * as fromApp from '@store';
import { selectCurrentUser } from '@store/auth/auth.selectors';
import { User } from '@shared/models';
import { SpinnerService } from '@shared/spinner/spinner.service';
import { RegexConstants } from '@shared/constants/regex.constants';
import { ErrorHelperService } from '@core/helper/error-helper.service';
import { DialogYesNoComponent } from '@shared/dialogs/dialog-yes-no/dialog-yes-no.component';
import { UserService } from '@core/api/user.service';

@Component({
  selector: 'fc-new-site-step-one',
  templateUrl: './new-site-step-one.component.html',
  styleUrls: ['./new-site-step-one.component.scss'],
  standalone: false
})
export class NewSiteStepOneComponent implements OnInit {
  destroyRef = inject(DestroyRef);

  newSiteForm: UntypedFormGroup;

  currentUser: User = null;

  emailError: string;
  phoneNumberError: boolean;

  constructor(
    public dialog: MatDialog,
    protected route: ActivatedRoute,
    readonly formBuilder: UntypedFormBuilder,
    readonly router: Router,
    readonly spinnerService: SpinnerService,
    readonly errorHelperService: ErrorHelperService,
    readonly formatHelper: FormatHelperService,
    readonly store: Store<fromApp.AppState>,
    readonly enrollmentGroupService: EnrollmentGroupService,
    readonly userService: UserService,
  ) {
    this.createNewSiteForm();
  }

  ngOnInit() {
    this.store
      .select(selectCurrentUser)
      .pipe(
        filter(currentUser => currentUser !== null),
        takeUntilDestroyed(this.destroyRef),
        take(1)
      )
      .subscribe(() => {
        this.router.navigate(['account', 'dashboard']);
      });
  }

  createNewSiteForm(): void {
    this.newSiteForm = this.formBuilder.group({
      email: ['', [Validators.maxLength(254), Validators.pattern(RegexConstants.EMAIL), Validators.required]],
      firstName: ['', [Validators.required, Validators.minLength(2)]],
      lastName: ['', [Validators.required, Validators.minLength(2)]],
      phone: ['', [Validators.required, Validators.minLength(2)]],
      county: ['', Validators.required],
      acres: ['', Validators.required],
      hiddenCheck: null,
    });

    this.newSiteForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.emailError = this.errorHelperService.getEmailErrorMessage(this.newSiteForm.controls.email);
    });
  }

  formatPhone($event: any) {
    this.phoneNumberError = this.formatHelper.formatPhone($event, this.newSiteForm.controls.phone)
  }

  checkAcreageAndProceed(): void {
    this.spinnerService.show('app-spinner');
    this.newSiteForm.markAsPristine();
    let email = this.newSiteForm.controls.email.value;
    this.userService.exists(email).subscribe(
      (exists: boolean) => {
        if (exists) {
          this.spinnerService.hide('app-spinner');
          this.dialog
            .open(DialogYesNoComponent, {
              panelClass: 'fc-yes-no-dialog',
              data: {
                title: 'A user with this email already exists',
                buttonTextYes: 'Login',
                buttonTextNo: 'Cancel',
              },
            })
            .afterClosed()
            .subscribe(result => {
              if (result) {
                this.router.navigate(['auth'], { queryParams: { email } });
              }
            });
        } else {
          const body = {
            email: email.toLowerCase(),
            first_name: this.newSiteForm.controls.firstName.value.trim(),
            last_name: this.newSiteForm.controls.lastName.value.trim(),
            phone: this.newSiteForm.controls.phone.value.trim(),
            initial_data: {
              'county': this.newSiteForm.controls.county.value,
              'acres': this.newSiteForm.controls.acres.value
            },
          };
          this.userService
            .requestUserOpen(body)
            .pipe(finalize(() => this.spinnerService.hide('app-spinner')))
            .subscribe(() => {
              this.router.navigate(['create-account'], {
                queryParams: {
                  username: email,
                },
              });
            });
        }
      });
  }

  // Form getters

  get email(): AbstractControl {
    return this.newSiteForm.get('email');
  }

  get acres(): AbstractControl {
    return this.newSiteForm.get('acres');
  }

  get county(): AbstractControl {
    return this.newSiteForm.get('county');
  }

  get isFormDirty(): boolean {
    return this.acres.dirty || this.county.dirty;
  }
}
